import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

interface UploadComplementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (xmlFile: File, pdfFile?: File) => Promise<void>;
  paymentInfo: {
    invoiceNumber: string;
    amount: number;
    referenceNumber: string;
  };
  isReplacing?: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export const UploadComplementModal: React.FC<UploadComplementModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  paymentInfo,
  isReplacing = false,
}) => {
  const [xmlFile, setXmlFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const validateFile = (file: File, expectedType: 'xml' | 'pdf'): string | null => {
    // Validar tamaño
    if (file.size > MAX_FILE_SIZE) {
      return `El archivo excede el tamaño máximo de 10 MB`;
    }

    // Validar extensión
    if (expectedType === 'xml' && !file.name.toLowerCase().endsWith('.xml')) {
      return 'El archivo debe ser de tipo XML';
    }
    if (expectedType === 'pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      return 'El archivo debe ser de tipo PDF';
    }

    return null;
  };

  const handleXmlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file, 'xml');
    if (validationError) {
      setError(validationError);
      setXmlFile(null);
      return;
    }

    setXmlFile(file);
    setError(null);
  };

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file, 'pdf');
    if (validationError) {
      setError(validationError);
      setPdfFile(null);
      return;
    }

    setPdfFile(file);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!xmlFile) {
      setError('El archivo XML es obligatorio');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);
      await onSubmit(xmlFile, pdfFile || undefined);
      handleClose();
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error al subir el complemento');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setXmlFile(null);
    setPdfFile(null);
    setError(null);
    setIsUploading(false);
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <CapaDeModal>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isReplacing ? 'Reemplazar Complemento' : 'Subir Complemento de Pago'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {isReplacing ? 'Los archivos actuales serán reemplazados' : 'Adjunta los archivos del complemento'}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isUploading}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Payment Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-600">Factura:</p>
                <p className="font-semibold text-gray-900">{paymentInfo.invoiceNumber}</p>
              </div>
              <div>
                <p className="text-gray-600">Monto:</p>
                <p className="font-semibold text-gray-900">
                  ${paymentInfo.amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-gray-600">Referencia:</p>
                <p className="font-semibold text-gray-900">{paymentInfo.referenceNumber}</p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* XML File Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Archivo XML <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
              {!xmlFile ? (
                <label className="cursor-pointer flex flex-col items-center">
                  <input
                    type="file"
                    accept=".xml,text/xml"
                    onChange={handleXmlChange}
                    disabled={isUploading}
                    className="hidden"
                  />
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Click para seleccionar archivo XML</span>
                  <span className="text-xs text-gray-500 mt-1">Máximo 10 MB</span>
                </label>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{xmlFile.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(xmlFile.size)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setXmlFile(null)}
                    disabled={isUploading}
                    className="text-red-600 hover:text-red-700 text-sm disabled:opacity-50"
                  >
                    Eliminar
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* PDF File Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Archivo PDF <span className="text-gray-500 text-xs">(opcional)</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
              {!pdfFile ? (
                <label className="cursor-pointer flex flex-col items-center">
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handlePdfChange}
                    disabled={isUploading}
                    className="hidden"
                  />
                  <FileText className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Click para seleccionar archivo PDF</span>
                  <span className="text-xs text-gray-500 mt-1">Máximo 10 MB</span>
                </label>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{pdfFile.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(pdfFile.size)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setPdfFile(null)}
                    disabled={isUploading}
                    className="text-red-600 hover:text-red-700 text-sm disabled:opacity-50"
                  >
                    Eliminar
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              disabled={isUploading}
              className={estiloDeBoton('secundario', 'flex-1')}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={!xmlFile || isUploading}
              className={estiloDeBoton('primario', 'flex-1')}
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  {isReplacing ? 'Reemplazar' : 'Subir Complemento'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </CapaDeModal>
  );
};
